<template>
  <div class="media-player">
    <vue-plyr ref="ttsplyr" class="non-highlightable">
      <audio ref="audio" :data-plyr-config="plyrConfigControls">
        <source :src="src" type="audio/mp3" />
      </audio>
    </vue-plyr>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, useTemplateRef } from "vue";
import VuePlyrPlugin from "@plugins/plyr/plyr.ts";
import DOMUtils from "@utils/dom.utils";
import i18n from "@plugins/i18n";
import { useEventBusWatch } from "@plugins/common/composables/event-bus-watch.ts";
import { AudioPlayer } from "@plugins/richtext/types.ts";
import appConfigs from "@configs/app.ts";

defineProps<{ src: string }>();

const emit = defineEmits<{
  (e: "mediaPlayable", payload: { player: AudioPlayer; id: string | number | undefined }): void;
  (e: "mediaLoaded"): void;
  (e: "mediaEnded"): void;
}>();

const { trackWatch } = useEventBusWatch();

const ttsPlyr = useTemplateRef("ttsplyr");

const player = computed<AudioPlayer | undefined>(
  () => (ttsPlyr.value as unknown as { player?: AudioPlayer })?.player,
);

const plyrConfigControls = computed(() => {
  // https://github.com/sampotts/plyr/blob/master/CONTROLS.md
  const controls = [
    "play-large", // The large play button in the center
    "play", // Play/pause playback
    "progress", // The progress bar and scrubber for playback and buffering
    "duration", // The full duration of the media
    "mute",
    "volume",
    "settings", // If the default controls are used, you can specify which settings to show in the menu ['captions', 'quality', 'speed', 'loop']
    "fullscreen", // Toggle fullscreen
  ];
  return JSON.stringify({
    controls: controls,
    iconUrl: `${appConfigs.vleDomain}/plyr/plyr.svg?version=3.6.2`,
    settings: ["speed"],
    speed: { selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5, 2] },
  });
});

const element = computed<HTMLElement | null>(() => {
  const el = (ttsPlyr.value as unknown as { $el?: HTMLElement })?.$el;
  return el ?? null;
});

const controlWrapperElement = computed<HTMLElement | null>(() => {
  return element.value ? (element.value.closest(".plyr") as HTMLElement | null) : null;
});

/* methods (keep selectors exactly the same) */
function backToStartTimeAndPause() {
  if (!player.value) return;
  player.value.currentTime = 0;
  player.value.pause();
}

function getDuration(): number {
  return player.value?.duration ?? 0;
}

function getCurrentTime(): number {
  return player.value?.currentTime ?? 0;
}

function destroy() {
  player.value?.destroy();
}

function allowEdit() {
  const wrapper = element.value?.closest(".tts-audio-wrapper") as HTMLElement | null;
  if (!wrapper) return;
  const plyrControl = DOMUtils.elOf(wrapper, "div.plyr__progress")[0];
  if (plyrControl) {
    plyrControl.setAttribute("contenteditable", "true");
  }
}

function resetSpeedToDefault() {
  if (!player.value) return;
  player.value.speed = 1;
}

function renameSpeedPopUpAndPanelText() {
  if (!player.value || !controlWrapperElement.value) return;
  const playerId = player.value.id;

  const playbackSpeedText = DOMUtils.elOf(
    controlWrapperElement.value,
    `#plyr-settings-${playerId}-home .plyr__control.plyr__control--forward > span`,
  );
  // in popup layer 1
  if (playbackSpeedText[0]?.firstChild) {
    playbackSpeedText[0].firstChild.textContent = i18n.t("plyr_label_playback_speed_label");
  }

  // in popup layer 2
  const buttonBackText = DOMUtils.elOf(
    controlWrapperElement.value,
    `#plyr-settings-${playerId}-speed .plyr__control.plyr__control--back > span`,
  );
  if (buttonBackText[0]?.firstChild) {
    buttonBackText[0].firstChild.textContent = i18n.t("button_back");
  }

  const timesOneSpeedTextLayerTwo = DOMUtils.elOf(
    controlWrapperElement.value,
    `#plyr-settings-${playerId}-speed [role="menu"] [role="menuitemradio"][value="1"] > span`,
  );
  if (timesOneSpeedTextLayerTwo[0]?.firstChild) {
    timesOneSpeedTextLayerTwo[0].firstChild.textContent = i18n.t(
      "plyr_label_normal_playback_speed_label",
    );
  }
  renamePlaybackSpeedOfOne();
}

function renamePlaybackSpeedOfOne() {
  if (!player.value || !controlWrapperElement.value) return;
  const playerId = player.value.id;
  // in popup layer 1
  const timesOneSpeedTextLayerOne = DOMUtils.elOf(
    controlWrapperElement.value,
    `#plyr-settings-${playerId}-home .plyr__control.plyr__control--forward > span .plyr__menu__value`,
  );
  if (timesOneSpeedTextLayerOne[0]?.firstChild) {
    timesOneSpeedTextLayerOne[0].firstChild.textContent = i18n.t(
      "plyr_label_normal_playback_speed_label",
    );
  }
}

onMounted(() => {
  if (!player.value) return;

  player.value.on("ratechange", () => {
    if (player.value && player.value.speed === 1) {
      // needs to be overwritten because plyr library will set text of x1 speed as 'Normal' via script
      renamePlaybackSpeedOfOne();
    }
  });

  player.value.once("canplay", () => {
    resetSpeedToDefault();
  });

  player.value.once("loadedmetadata", () => {
    emit("mediaPlayable", { player: player.value as AudioPlayer, id: player.value?.id });
    allowEdit();
  });

  player.value.once("progress", () => {
    emit("mediaLoaded");
  });

  player.value.on("ended", () => {
    backToStartTimeAndPause();
    emit("mediaEnded");
  });

  player.value.on("timeupdate", () => {
    //stop video at end cuepoint when user is not seeking
    if (!player.value) return;
    if (player.value.seeking) return;
    const cueEnd = (ttsPlyr.value as unknown as { cueEnd?: number })?.cueEnd ?? 0;
    if (cueEnd > 0 && player.value.currentTime >= cueEnd) {
      backToStartTimeAndPause();
    }
  });

  trackWatch(VuePlyrPlugin.eventBus.$on("destroy-all", destroy));
  renameSpeedPopUpAndPanelText();
});

onBeforeUnmount(() => {
  destroy();
});

defineExpose({
  backToStartTimeAndPause,
  getDuration,
  getCurrentTime,
  destroy,
  allowEdit,
  resetSpeedToDefault,
  renameSpeedPopUpAndPanelText,
  renamePlaybackSpeedOfOne,
  player,
  plyrConfigControls,
});
</script>
